import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import subscriptionService from '@/services/subscriptionService';
import type {
  SubscriptionPlan,
  Subscription,
  Payment,
  UserSubscription,
  SubscriptionQueryDto,
  PaymentHistoryQueryDto,
  CreateSubscriptionDto,
  CreatePaymentIntentDto,
  CreateCheckoutSessionDto,
} from '@/types/subscription';
import type { ProcessCheckoutSessionResponse } from '@/types/subscription';

interface SubscriptionState {
  // Subscription Plans
  plans: SubscriptionPlan[];
  plansLoading: boolean;
  plansError: string | null;

  // User Subscriptions
  userSubscriptions: UserSubscription[];
  userSubscriptionsLoading: boolean;
  userSubscriptionsError: string | null;
  userSubscriptionsPagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };

  // Organization Subscriptions
  organizationSubscriptions: Subscription[];
  organizationSubscriptionsLoading: boolean;
  organizationSubscriptionsError: string | null;
  organizationSubscriptionsPagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };

  // Payment History
  paymentHistory: Payment[];
  paymentHistoryLoading: boolean;
  paymentHistoryError: string | null;
  paymentHistoryPagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };

  // Current Operations
  currentSubscription: Subscription | null;
  currentPlan: SubscriptionPlan | null;
  checkoutSession: { id: string; url: string; status: string } | null;
  operationLoading: boolean;
  operationError: string | null;
  processedSession?: ProcessCheckoutSessionResponse | null;
  pathways: Array<{
    _id: string;
    name: string;
    key: string;
    isActive: boolean;
  }>;
}

const initialState: SubscriptionState = {
  // Subscription Plans
  plans: [],
  plansLoading: false,
  plansError: null,

  // User Subscriptions
  userSubscriptions: [],
  userSubscriptionsLoading: false,
  userSubscriptionsError: null,
  userSubscriptionsPagination: {
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  },

  // Organization Subscriptions
  organizationSubscriptions: [],
  organizationSubscriptionsLoading: false,
  organizationSubscriptionsError: null,
  organizationSubscriptionsPagination: {
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  },

  // Payment History
  paymentHistory: [],
  paymentHistoryLoading: false,
  paymentHistoryError: null,
  paymentHistoryPagination: {
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  },

  // Current Operations
  currentSubscription: null,
  currentPlan: null,
  checkoutSession: null,
  operationLoading: false,
  operationError: null,
  processedSession: null,
  pathways: [],
};

export const fetchPathways = createAsyncThunk(
  'stripe/fetchPathways',
  async (isActive: boolean = true, { rejectWithValue }) => {
    try {
      const response = await subscriptionService.getPathways(isActive);
      return response;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to fetch plans'
      );
    }
  }
);

// Async thunks
export const fetchSubscriptionPlans = createAsyncThunk(
  'subscription/fetchPlans',
  async (_, { rejectWithValue }) => {
    try {
      const plans = await subscriptionService.getSubscriptionPlans();
      return plans;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to fetch plans'
      );
    }
  }
);

export const fetchSubscriptionPlansByOrganization = createAsyncThunk(
  'subscription/fetchPlansByOrganization',
  async (organizationId: string, { rejectWithValue }) => {
    try {
      const plans =
        await subscriptionService.getSubscriptionPlansByOrganization(
          organizationId
        );
      return plans;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error
          ? error.message
          : 'Failed to fetch organization plans'
      );
    }
  }
);

export const fetchSubscriptionPlanById = createAsyncThunk(
  'subscription/fetchPlanById',
  async (id: string, { rejectWithValue }) => {
    try {
      const plan = await subscriptionService.getSubscriptionPlanById(id);
      return plan;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to fetch plan'
      );
    }
  }
);

export const fetchUserSubscriptions = createAsyncThunk(
  'subscription/fetchUserSubscriptions',
  async (query: SubscriptionQueryDto, { rejectWithValue }) => {
    try {
      const response = await subscriptionService.getUserSubscriptions(query);
      return response;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error
          ? error.message
          : 'Failed to fetch user subscriptions'
      );
    }
  }
);

export const fetchOrganizationSubscriptions = createAsyncThunk(
  'subscription/fetchOrganizationSubscriptions',
  async (query: SubscriptionQueryDto, { rejectWithValue }) => {
    try {
      const response =
        await subscriptionService.getOrganizationSubscriptions(query);
      return response;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error
          ? error.message
          : 'Failed to fetch organization subscriptions'
      );
    }
  }
);

export const fetchPaymentHistory = createAsyncThunk(
  'subscription/fetchPaymentHistory',
  async (query: PaymentHistoryQueryDto, { rejectWithValue }) => {
    try {
      const response = await subscriptionService.getPaymentHistory(query);
      return response;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error
          ? error.message
          : 'Failed to fetch payment history'
      );
    }
  }
);

export const createSubscription = createAsyncThunk(
  'subscription/createSubscription',
  async (data: CreateSubscriptionDto, { rejectWithValue }) => {
    try {
      const subscription = await subscriptionService.createSubscription(data);
      return subscription;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to create subscription'
      );
    }
  }
);

export const createPaymentIntent = createAsyncThunk(
  'subscription/createPaymentIntent',
  async (data: CreatePaymentIntentDto, { rejectWithValue }) => {
    try {
      const response = await subscriptionService.createPaymentIntent(data);
      return response;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error
          ? error.message
          : 'Failed to create payment intent'
      );
    }
  }
);

export const createCheckoutSession = createAsyncThunk(
  'subscription/createCheckoutSession',
  async (data: CreateCheckoutSessionDto, { rejectWithValue }) => {
    try {
      const session = await subscriptionService.createCheckoutSession(data);
      return session;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error
          ? error.message
          : 'Failed to create checkout session'
      );
    }
  }
);

export const processCheckoutSession = createAsyncThunk(
  'subscription/processCheckoutSession',
  async (sessionId: string, { rejectWithValue }) => {
    try {
      const service = (await import('@/services/subscriptionService')).default;
      const response = await service.processCheckoutSession(sessionId);
      return response as ProcessCheckoutSessionResponse;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error
          ? error.message
          : 'Failed to process checkout session'
      );
    }
  }
);

export const cancelSubscription = createAsyncThunk(
  'subscription/cancelSubscription',
  async (subscriptionId: string, { rejectWithValue }) => {
    try {
      await subscriptionService.cancelSubscription(subscriptionId);
      return subscriptionId;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to cancel subscription'
      );
    }
  }
);

export const updateSubscriptionQuantity = createAsyncThunk(
  'subscription/updateQuantity',
  async (
    { subscriptionId, quantity }: { subscriptionId: string; quantity: number },
    { rejectWithValue }
  ) => {
    try {
      const subscription = await subscriptionService.updateSubscriptionQuantity(
        subscriptionId,
        quantity
      );
      return subscription;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error
          ? error.message
          : 'Failed to update subscription quantity'
      );
    }
  }
);

const subscriptionSlice = createSlice({
  name: 'subscription',
  initialState,
  reducers: {
    // Reset states
    resetPlans: (state) => {
      state.plans = [];
      state.plansLoading = false;
      state.plansError = null;
    },
    resetUserSubscriptions: (state) => {
      state.userSubscriptions = [];
      state.userSubscriptionsLoading = false;
      state.userSubscriptionsError = null;
      state.userSubscriptionsPagination = {
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 0,
      };
    },
    resetOrganizationSubscriptions: (state) => {
      state.organizationSubscriptions = [];
      state.organizationSubscriptionsLoading = false;
      state.organizationSubscriptionsError = null;
      state.organizationSubscriptionsPagination = {
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 0,
      };
    },
    resetPaymentHistory: (state) => {
      state.paymentHistory = [];
      state.paymentHistoryLoading = false;
      state.paymentHistoryError = null;
      state.paymentHistoryPagination = {
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 0,
      };
    },
    resetOperationState: (state) => {
      state.currentSubscription = null;
      state.currentPlan = null;
      state.checkoutSession = null;
      state.operationLoading = false;
      state.operationError = null;
    },
    resetAll: (state) => {
      Object.assign(state, initialState);
    },

    // Set current items
    setCurrentPlan: (state, action: PayloadAction<SubscriptionPlan>) => {
      state.currentPlan = action.payload;
    },
    setCurrentSubscription: (state, action: PayloadAction<Subscription>) => {
      state.currentSubscription = action.payload;
    },
    setCheckoutSession: (
      state,
      action: PayloadAction<{ id: string; url: string; status: string } | null>
    ) => {
      state.checkoutSession = action.payload;
    },

    // Update pagination
    updateUserSubscriptionsPagination: (
      state,
      action: PayloadAction<{ page?: number; limit?: number }>
    ) => {
      if (action.payload.page !== undefined) {
        state.userSubscriptionsPagination.page = action.payload.page;
      }
      if (action.payload.limit !== undefined) {
        state.userSubscriptionsPagination.limit = action.payload.limit;
      }
    },
    updateOrganizationSubscriptionsPagination: (
      state,
      action: PayloadAction<{ page?: number; limit?: number }>
    ) => {
      if (action.payload.page !== undefined) {
        state.organizationSubscriptionsPagination.page = action.payload.page;
      }
      if (action.payload.limit !== undefined) {
        state.organizationSubscriptionsPagination.limit = action.payload.limit;
      }
    },
    updatePaymentHistoryPagination: (
      state,
      action: PayloadAction<{ page?: number; limit?: number }>
    ) => {
      if (action.payload.page !== undefined) {
        state.paymentHistoryPagination.page = action.payload.page;
      }
      if (action.payload.limit !== undefined) {
        state.paymentHistoryPagination.limit = action.payload.limit;
      }
    },
  },
  extraReducers: (builder) => {
    // Fetch Subscription Plans
    builder
      .addCase(fetchSubscriptionPlans.pending, (state) => {
        state.plansLoading = true;
        state.plansError = null;
      })
      .addCase(fetchSubscriptionPlans.fulfilled, (state, action) => {
        state.plansLoading = false;
        state.plans = action.payload;
      })
      .addCase(fetchSubscriptionPlans.rejected, (state, action) => {
        state.plansLoading = false;
        state.plansError = action.payload as string;
      });

    // Fetch Subscription Plans by Organization
    builder
      .addCase(fetchSubscriptionPlansByOrganization.pending, (state) => {
        state.plansLoading = true;
        state.plansError = null;
      })
      .addCase(
        fetchSubscriptionPlansByOrganization.fulfilled,
        (state, action) => {
          state.plansLoading = false;
          state.plans = action.payload;
        }
      )
      .addCase(
        fetchSubscriptionPlansByOrganization.rejected,
        (state, action) => {
          state.plansLoading = false;
          state.plansError = action.payload as string;
        }
      );

    // Fetch single plan by id
    builder
      .addCase(fetchSubscriptionPlanById.pending, (state) => {
        state.plansLoading = true;
        state.plansError = null;
      })
      .addCase(fetchSubscriptionPlanById.fulfilled, (state, action) => {
        state.plansLoading = false;
        const existingIndex = state.plans.findIndex(
          (p) => p._id === action.payload._id
        );
        if (existingIndex !== -1) {
          state.plans[existingIndex] = action.payload;
        } else {
          state.plans.push(action.payload);
        }
        state.currentPlan = action.payload;
      })
      .addCase(fetchSubscriptionPlanById.rejected, (state, action) => {
        state.plansLoading = false;
        state.plansError = action.payload as string;
      });

    // Fetch User Subscriptions
    builder
      .addCase(fetchUserSubscriptions.pending, (state) => {
        state.userSubscriptionsLoading = true;
        state.userSubscriptionsError = null;
      })
      .addCase(fetchUserSubscriptions.fulfilled, (state, action) => {
        state.userSubscriptionsLoading = false;
        state.userSubscriptions = action.payload.data;
        state.userSubscriptionsPagination = {
          page: action.payload.page,
          limit: action.payload.limit,
          total: action.payload.total,
          totalPages: action.payload.totalPages,
        };
      })
      .addCase(fetchUserSubscriptions.rejected, (state, action) => {
        state.userSubscriptionsLoading = false;
        state.userSubscriptionsError = action.payload as string;
      });

    // Fetch Organization Subscriptions
    builder
      .addCase(fetchOrganizationSubscriptions.pending, (state) => {
        state.organizationSubscriptionsLoading = true;
        state.organizationSubscriptionsError = null;
      })
      .addCase(fetchOrganizationSubscriptions.fulfilled, (state, action) => {
        state.organizationSubscriptionsLoading = false;
        state.organizationSubscriptions = action.payload.data;
        state.organizationSubscriptionsPagination = {
          page: action.payload.page,
          limit: action.payload.limit,
          total: action.payload.total,
          totalPages: action.payload.totalPages,
        };
      })
      .addCase(fetchOrganizationSubscriptions.rejected, (state, action) => {
        state.organizationSubscriptionsLoading = false;
        state.organizationSubscriptionsError = action.payload as string;
      });

    // Fetch Payment History
    builder
      .addCase(fetchPaymentHistory.pending, (state) => {
        state.paymentHistoryLoading = true;
        state.paymentHistoryError = null;
      })
      .addCase(fetchPaymentHistory.fulfilled, (state, action) => {
        state.paymentHistoryLoading = false;
        state.paymentHistory = action.payload.data;
        state.paymentHistoryPagination = {
          page: action.payload.page,
          limit: action.payload.limit,
          total: action.payload.total,
          totalPages: action.payload.totalPages,
        };
      })
      .addCase(fetchPaymentHistory.rejected, (state, action) => {
        state.paymentHistoryLoading = false;
        state.paymentHistoryError = action.payload as string;
      });

    // Create Subscription
    builder
      .addCase(createSubscription.pending, (state) => {
        state.operationLoading = true;
        state.operationError = null;
      })
      .addCase(createSubscription.fulfilled, (state, action) => {
        state.operationLoading = false;
        state.currentSubscription = action.payload;
        // Add to organization subscriptions if not already present
        const exists = state.organizationSubscriptions.find(
          (sub) => sub._id === action.payload._id
        );
        if (!exists) {
          state.organizationSubscriptions.unshift(action.payload);
        }
      })
      .addCase(createSubscription.rejected, (state, action) => {
        state.operationLoading = false;
        state.operationError = action.payload as string;
      });

    // Create Payment Intent
    builder
      .addCase(createPaymentIntent.pending, (state) => {
        state.operationLoading = true;
        state.operationError = null;
      })
      .addCase(createPaymentIntent.fulfilled, (state) => {
        state.operationLoading = false;
      })
      .addCase(createPaymentIntent.rejected, (state, action) => {
        state.operationLoading = false;
        state.operationError = action.payload as string;
      });

    // Create Checkout Session
    builder
      .addCase(createCheckoutSession.pending, (state) => {
        state.operationLoading = true;
        state.operationError = null;
      })
      .addCase(createCheckoutSession.fulfilled, (state, action) => {
        state.operationLoading = false;
        state.checkoutSession = action.payload;
      })
      .addCase(createCheckoutSession.rejected, (state, action) => {
        state.operationLoading = false;
        state.operationError = action.payload as string;
      });

    // Process Checkout Session
    builder
      .addCase(processCheckoutSession.pending, (state) => {
        state.operationLoading = true;
        state.operationError = null;
      })
      .addCase(processCheckoutSession.fulfilled, (state, action) => {
        state.operationLoading = false;
        state.processedSession = action.payload;
      })
      .addCase(processCheckoutSession.rejected, (state, action) => {
        state.operationLoading = false;
        state.operationError = action.payload as string;
      });

    // Cancel Subscription
    builder
      .addCase(cancelSubscription.pending, (state) => {
        state.operationLoading = true;
        state.operationError = null;
      })
      .addCase(cancelSubscription.fulfilled, (state, action) => {
        state.operationLoading = false;
        // Update the subscription status in the list
        const subscriptionIndex = state.organizationSubscriptions.findIndex(
          (sub) => sub._id === action.payload
        );
        if (subscriptionIndex !== -1) {
          state.organizationSubscriptions[subscriptionIndex].status =
            'canceled';
          state.organizationSubscriptions[subscriptionIndex].isActive = false;
        }
      })
      .addCase(cancelSubscription.rejected, (state, action) => {
        state.operationLoading = false;
        state.operationError = action.payload as string;
      });

    // Update Subscription Quantity
    builder
      .addCase(updateSubscriptionQuantity.pending, (state) => {
        state.operationLoading = true;
        state.operationError = null;
      })
      .addCase(updateSubscriptionQuantity.fulfilled, (state, action) => {
        state.operationLoading = false;
        // Update the subscription in the list
        const subscriptionIndex = state.organizationSubscriptions.findIndex(
          (sub) => sub._id === action.payload._id
        );
        if (subscriptionIndex !== -1) {
          state.organizationSubscriptions[subscriptionIndex] = action.payload;
        }
      })
      .addCase(updateSubscriptionQuantity.rejected, (state, action) => {
        state.operationLoading = false;
        state.operationError = action.payload as string;
      });

    // Fetch pathways
    builder
      .addCase(
        fetchPathways.fulfilled,
        (
          state,
          action: PayloadAction<
            Array<{ _id: string; name: string; key: string; isActive: boolean }>
          >
        ) => {
          state.pathways = action.payload;
        }
      )
      .addCase(fetchPathways.rejected, (state) => {
        state.pathways = [];
      });
  },
});

export const {
  resetPlans,
  resetUserSubscriptions,
  resetOrganizationSubscriptions,
  resetPaymentHistory,
  resetOperationState,
  resetAll,
  setCurrentPlan,
  setCurrentSubscription,
  setCheckoutSession,
  updateUserSubscriptionsPagination,
  updateOrganizationSubscriptionsPagination,
  updatePaymentHistoryPagination,
} = subscriptionSlice.actions;

export default subscriptionSlice.reducer;
